/**
 * Dynamic package (flight+hotel) — display-only normalized pricing.
 * Rules: no cross-currency conversion, no summing flight+hotel to invent totals,
 * אסור: deal.price, hotel.* (אובייקט מלון LDS/Gemonix), linkedFlightsDealPrice, כל fallback אחר —
 * במקום: "לא זמין כרגע" + [FALLBACK_BLOCKED].
 *
 * Enable verbose logs: localStorage.setItem('DYNAMIC_PACKAGE_PRICING_LOG','1')
 */

import {
  agentHotelSearchLowestPackagePrice,
  formatAgentHotelSearchApiPriceDisplay,
} from "../agentHotelSearchApiPrice";
import { normalizeHotelSearchCurrencyCode } from "../../constants/hotelCurrencies";
import {
  getHotelProfilePackageCurrency,
  getHotelProfilePackageSupplierUsd,
} from "../hotelSellingPrice";
import { applyFXSettlement } from "../../pricing/pricingEngine.js";
import { formatIlsDisplay } from "../formatIlsDisplay";

export const DYNAMIC_PACKAGE_PRICING_LOG_LS = "DYNAMIC_PACKAGE_PRICING_LOG";
export const DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE = "לא זמין כרגע";

export function isDynamicPackagePricingLogEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem(DYNAMIC_PACKAGE_PRICING_LOG_LS) === "1"
    );
  } catch {
    return false;
  }
}

export function logDynamicPackagePricing(tag, payload) {
  if (!isDynamicPackagePricingLogEnabled()) return;
  try {
    console.info("[DYNAMIC_PACKAGE_PRICING]", tag, {
      t: new Date().toISOString(),
      ...payload,
    });
  } catch {
    /* ignore */
  }
}

/**
 * ניסיון להשלים מחיר משדה אסור / חסר — לא משתמשים בערך, רק לוג.
 */
export function logFallbackBlocked(source, targetDisplayField, reason, extra = {}) {
  const row = {
    source,
    targetDisplayField,
    reason,
    t: new Date().toISOString(),
    ...extra,
  };
  try {
    console.warn("[FALLBACK_BLOCKED]", row);
  } catch {
    /* ignore */
  }
  logDynamicPackagePricing("FALLBACK_BLOCKED", row);
}

/** שם יצוא תואם קוד ישן (למשל summary.jsx) — זהה ל־logFallbackBlocked */
export const logDynamicPackageFallback = logFallbackBlocked;

function strictPositiveNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * ISO currency from fareDetails / dealFare (supplier), no guessing from unrelated objects.
 * @param {object|null|undefined} fullDeal
 * @returns {string|null}
 */
export function pickDynamicPackageSupplierCurrency(fullDeal) {
  if (!fullDeal || typeof fullDeal !== "object") return null;
  const fd = fullDeal.fareDetails;
  const candidates = [
    fd?.currencyCode,
    fd?.CurrencyCode,
    fd?.currency,
    fullDeal.dealFare?.currency,
    fullDeal.dealFare?.Currency,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const u = String(c).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(u)) return u;
  }
  return null;
}

/**
 * Flight / hotel / taxes / total מ־dealFare בלבד.
 * אסור: deal.price, hotel.*, כל מחיר מחוץ ל־dealFare.
 * Outbound+inbound: סכום רק כששני השדות קיימים ב־dealFare.
 */
/**
 * LDS deal may expose `dealFare` on the root or under `fullDeal`.
 * @param {object|null|undefined} entity
 * @returns {object|null}
 */
export function resolveDynamicPackagePricingDealRoot(entity) {
  if (!entity || typeof entity !== "object") return null;
  if (entity.dealFare && typeof entity.dealFare === "object") return entity;
  if (entity.fullDeal?.dealFare && typeof entity.fullDeal.dealFare === "object") {
    return entity.fullDeal;
  }
  return null;
}

/**
 * למה שורת מחיר הטיסה נופלת ל־"לא זמין כרגע" (דיבוג / לוגים בלבד).
 * @typedef {"missing_dealFare_flightFare"|"non_numeric_flight_price"|"blocked_by_normalizer"|"missing_currency"|"fallback_disabled"|"source_not_found"|null} DynamicPackageFlightUnavailableReason
 */

/**
 * @param {object} p
 * @param {object|null} p.dealRoot
 * @param {ReturnType<typeof extractDynamicPackageDealFareComponents>} p.dealComponents
 * @param {string|null} p.supplierCurrency
 * @param {number|null|undefined} p.linkedFlightsDealPriceIls
 * @param {boolean} [p.hasLinkedFlightsStructure]
 * @param {boolean} [p.normalizedPricingEnabled]
 * @param {string|null} [p.stage]
 * @param {number|null} [p.searchCardFlightAmount]
 * @param {string|null} [p.searchCardFlightCurrency]
 * @param {string|null} [p.searchCardFlightPickFailureReason]
 * @returns {DynamicPackageFlightUnavailableReason}
 */
export function resolveFlightPriceUnavailableReason({
  dealRoot,
  dealComponents,
  supplierCurrency,
  linkedFlightsDealPriceIls,
  hasLinkedFlightsStructure = false,
  normalizedPricingEnabled = false,
  stage = null,
  searchCardFlightAmount = null,
  searchCardFlightCurrency = null,
  searchCardFlightPickFailureReason = null,
}) {
  if (
    stage === "search_card" &&
    searchCardFlightAmount != null &&
    isIso4217(searchCardFlightCurrency)
  ) {
    return null;
  }
  if (stage === "search_card") {
    return searchCardFlightPickFailureReason ?? "search_card_flight_unavailable";
  }

  const flightAmt = dealComponents?.flight;
  if (flightAmt != null) {
    return isIso4217(supplierCurrency) ? null : "missing_currency";
  }

  const df = dealRoot?.dealFare;
  if (!dealRoot || !df || typeof df !== "object") {
    return "source_not_found";
  }

  const flightKeyNames = [
    "flightFare",
    "flightsFare",
    "airFare",
    "outboundFare",
    "inboundFare",
  ];
  let sawFlightKey = false;
  for (const k of flightKeyNames) {
    const v = df[k];
    if (v == null || v === "") continue;
    sawFlightKey = true;
    if (strictPositiveNumber(v) == null) {
      return "non_numeric_flight_price";
    }
  }

  const linkedNum =
    linkedFlightsDealPriceIls != null && linkedFlightsDealPriceIls !== ""
      ? Number(linkedFlightsDealPriceIls)
      : NaN;
  if (Number.isFinite(linkedNum) && linkedNum > 0) {
    return "blocked_by_normalizer";
  }

  if (normalizedPricingEnabled && hasLinkedFlightsStructure) {
    return "fallback_disabled";
  }

  if (!sawFlightKey && strictPositiveNumber(df.adultFare) != null) {
    return "missing_dealFare_flightFare";
  }

  return "missing_dealFare_flightFare";
}

export function extractDynamicPackageDealFareComponents(fullDeal) {
  const df = fullDeal?.dealFare;
  if (!df || typeof df !== "object") {
    return {
      flight: null,
      hotel: null,
      taxesFees: null,
      packageTotal: null,
      flightCurrency: null,
    };
  }

  let flight =
    strictPositiveNumber(df.flightFare) ??
    strictPositiveNumber(df.flightsFare) ??
    strictPositiveNumber(df.airFare);
  const ob = strictPositiveNumber(df.outboundFare);
  const ib = strictPositiveNumber(df.inboundFare);
  if (flight == null && (ob != null || ib != null)) {
    const sum = (ob ?? 0) + (ib ?? 0);
    flight = sum > 0 ? sum : null;
  }

  const hotel =
    strictPositiveNumber(df.hotelFare) ??
    strictPositiveNumber(df.roomFare) ??
    strictPositiveNumber(df.accommodationFare);

  let taxesFees = null;
  const taxParts = [
    strictPositiveNumber(df.taxesTotal),
    strictPositiveNumber(df.taxFare),
    strictPositiveNumber(df.taxesFare),
    strictPositiveNumber(df.feesFare),
    strictPositiveNumber(df.feeFare),
    strictPositiveNumber(df.serviceFee),
    strictPositiveNumber(df.supplierFee),
  ].filter((x) => x != null);
  if (taxParts.length) {
    taxesFees = taxParts.reduce((a, b) => a + b, 0);
  }

  const packageTotal =
    strictPositiveNumber(df.totalFare) ??
    strictPositiveNumber(
      typeof df.totalPrice === "object" && df.totalPrice != null
        ? df.totalPrice.price
        : df.totalPrice
    );

  const flightCurrency = pickDynamicPackageSupplierCurrency(fullDeal);

  return {
    flight,
    hotel,
    taxesFees,
    packageTotal,
    flightCurrency,
  };
}

function formatLine(amount, currency) {
  if (amount == null) return null;
  const sym = formatAgentHotelSearchApiPriceDisplay(amount, currency);
  return sym ?? String(amount);
}

function isIso4217(c) {
  if (c == null || String(c).trim() === "") return false;
  return /^[A-Z]{3}$/.test(String(c).trim().toUpperCase());
}

/**
 * סכום + מטבע ISO בלבד; בלי ניחוש מטבע — אחרת "לא זמין" + [FALLBACK_BLOCKED].
 */
function displayMoneyOrUnavailable(amount, currency, targetDisplayField, blockedSource) {
  if (amount == null) return DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE;
  const cur = currency != null ? String(currency).trim().toUpperCase() : "";
  if (!isIso4217(cur)) {
    logFallbackBlocked(
      blockedSource,
      targetDisplayField,
      "missing or invalid ISO currency — display fallback blocked",
      { amountPresent: true }
    );
    return DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE;
  }
  return formatLine(amount, cur) ?? DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE;
}

/**
 * Normalized view model for UI + logging. No FX conversion.
 *
 * @param {object} p
 * @param {'search_card'|'profile_summary'|'order_summary'} p.stage
 * @param {object|null} [p.fullDeal] — deal root or wrapper (dealFare + fareDetails)
 * @param {object|null} [p.savedDeal] — SearchContext savedDeal (used if fullDeal omitted)
 * @param {object|null} [p.hotelsItem] — Gemonix hotel row (search)
 * @param {string|null} [p.searchListCurrency] — display currency for LowestPackagePrice (from search phrase)
 * @param {object|null} [p.selectedPackage] — Gemonix package on profile
 * @param {number|null} [p.linkedFlightsDealPriceIls] — נשמר ב־rawSource בלבד; לא לתצוגה — [FALLBACK_BLOCKED] אם חסר flight ב־dealFare
 * @param {boolean} [p.hasLinkedFlightsStructure] — האם ל־savedDeal יש linkedFlights (לדיבוג סיבת fallback_disabled)
 * @param {boolean} [p.normalizedPricingEnabled] — מצב normalized פעיל (לדיבוג סיבת fallback_disabled)
 * @param {{ amount?: number|null, currency?: string|null, source?: string|null, unavailableReason?: string|null, rawFlightSources?: object }|null} [p.searchCheapestFlight] — תוצאת pickCheapest ל־search_card בלבד
 */
export function buildNormalizedDynamicPackagePricing(p = {}) {
  const {
    stage = "unknown",
    fullDeal = null,
    savedDeal = null,
    hotelsItem = null,
    searchListCurrency = null,
    selectedPackage = null,
    linkedFlightsDealPriceIls = null,
    hasLinkedFlightsStructure = false,
    normalizedPricingEnabled = false,
    searchCheapestFlight = null,
  } = p;

  const dealRoot =
    resolveDynamicPackagePricingDealRoot(fullDeal) ||
    resolveDynamicPackagePricingDealRoot(savedDeal);

  const rowDealRoot =
    resolveDynamicPackagePricingDealRoot(hotelsItem) ||
    resolveDynamicPackagePricingDealRoot(hotelsItem?.fullDeal);

  const dealComponents = extractDynamicPackageDealFareComponents(dealRoot);
  const rowDealComponents = rowDealRoot
    ? extractDynamicPackageDealFareComponents(rowDealRoot)
    : null;
  const supplierCur = pickDynamicPackageSupplierCurrency(dealRoot);
  const rowSupplierCur = rowDealRoot
    ? pickDynamicPackageSupplierCurrency(rowDealRoot)
    : null;

  const hotelSearchAmount = agentHotelSearchLowestPackagePrice(hotelsItem);
  const listCurRaw =
    searchListCurrency != null && String(searchListCurrency).trim()
      ? String(searchListCurrency).trim().toUpperCase()
      : null;
  /** יישור עם formatAgentHotelSearchApiPriceDisplay / getHotelSearchCurrencyMeta (USD|ILS) */
  const listCurNormalized =
    listCurRaw != null && isIso4217(listCurRaw)
      ? listCurRaw
      : normalizeHotelSearchCurrencyCode(searchListCurrency);

  const profileHotelAmount = selectedPackage
    ? getHotelProfilePackageSupplierUsd(selectedPackage)
    : null;
  const profileHotelCur = selectedPackage
    ? getHotelProfilePackageCurrency(selectedPackage)
    : null;

  /** מלון: רשימת חיפוש (LowestPackagePrice+מטבע חיפוש) או dealFare או חבילת Gemonix — בלי hotel.* / deal.price / linkedFlights */
  let hotelAmount = null;
  let hotelCurrency = null;
  let hotelSource = null;

  if (stage === "search_card") {
    if (hotelSearchAmount != null) {
      if (!isIso4217(listCurNormalized)) {
        logFallbackBlocked(
          "searchListCurrency",
          "hotel",
          "LowestPackagePrice present but could not resolve hotel list currency"
        );
      } else {
        hotelAmount = hotelSearchAmount;
        hotelCurrency = listCurNormalized;
        hotelSource =
          listCurRaw && isIso4217(listCurRaw)
            ? "hotelsItem.LowestPackagePrice + searchListCurrency"
            : "hotelsItem.LowestPackagePrice + normalizeHotelSearchCurrencyCode(searchPhrase.currency)";
      }
    } else if (dealComponents.hotel != null) {
      if (!isIso4217(supplierCur)) {
        logFallbackBlocked(
          "fareDetails|dealFare.currency",
          "hotel",
          "dealFare hotel fare present but no supplier ISO currency — fallback blocked"
        );
      } else {
        hotelAmount = dealComponents.hotel;
        hotelCurrency = supplierCur;
        hotelSource =
          "dealFare.hotelFare|roomFare|accommodationFare + supplierCurrency";
      }
    }
  } else if (stage === "profile_summary" || stage === "order_summary") {
    if (profileHotelAmount != null) {
      if (!isIso4217(profileHotelCur)) {
        logFallbackBlocked(
          "package.PackagePrice.Currency|package currency fields",
          "hotel",
          "package amount present but no ISO currency on package row — supplierCurrency fallback blocked"
        );
      } else {
        hotelAmount = profileHotelAmount;
        hotelCurrency = profileHotelCur;
        hotelSource =
          "selectedPackage PackagePrice/SimplePrice/Rooms + package currency only";
      }
    } else if (dealComponents.hotel != null) {
      if (!isIso4217(supplierCur)) {
        logFallbackBlocked(
          "fareDetails|dealFare.currency",
          "hotel",
          "dealFare hotel fare present but no supplier ISO currency — fallback blocked"
        );
      } else {
        hotelAmount = dealComponents.hotel;
        hotelCurrency = supplierCur;
        hotelSource = "dealFare hotel fare + supplierCurrency";
      }
    }
  }

  let flightAmount = dealComponents.flight;
  let flightCurrency = dealComponents.flight != null ? supplierCur : null;
  let searchCardFlightSource = null;

  if (stage === "search_card") {
    const sc = searchCheapestFlight;
    if (sc && sc.amount != null && isIso4217(sc.currency)) {
      flightAmount = sc.amount;
      flightCurrency = String(sc.currency).trim().toUpperCase();
      searchCardFlightSource = sc.source ?? "search_min";
    } else if (
      rowDealComponents?.flight != null &&
      isIso4217(rowSupplierCur)
    ) {
      flightAmount = rowDealComponents.flight;
      flightCurrency = rowSupplierCur;
      searchCardFlightSource = "row_dealFare";
    } else {
      flightAmount = null;
      flightCurrency = null;
      searchCardFlightSource = null;
    }
  }

  const taxesAmount = dealComponents.taxesFees;
  const taxesCurrency = taxesAmount != null ? supplierCur : null;

  let packageTotalAmount = dealComponents.packageTotal;
  let packageTotalCurrency = packageTotalAmount != null ? supplierCur : null;
  let packageTotalSource = "dealFare.totalFare|dealFare.totalPrice";

  if (packageTotalAmount == null) {
    logDynamicPackagePricing("package_total_missing", {
      stage,
      note: "No dealFare total in strict paths — not using deal.price / hotel.* / linkedFlights",
    });
  }

  const display = {
    flightText:
      flightAmount == null
        ? DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE
        : displayMoneyOrUnavailable(
            flightAmount,
            flightCurrency,
            "flight",
            "fareDetails|dealFare.currency (flight)"
          ),
    hotelText: displayMoneyOrUnavailable(
      hotelAmount,
      hotelCurrency,
      "hotel",
      "hotel line currency"
    ),
    taxesText:
      taxesAmount == null
        ? DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE
        : displayMoneyOrUnavailable(
            taxesAmount,
            taxesCurrency,
            "taxes",
            "fareDetails|dealFare.currency (taxes)"
          ),
    packageTotalText:
      packageTotalAmount == null
        ? DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE
        : displayMoneyOrUnavailable(
            packageTotalAmount,
            packageTotalCurrency,
            "packageTotal",
            "fareDetails|dealFare.currency (total)"
          ),
  };

  let metaCurrency = null;
  if (dealRoot && isIso4217(supplierCur)) {
    metaCurrency = supplierCur;
  } else if (hotelAmount != null && isIso4217(hotelCurrency)) {
    metaCurrency = hotelCurrency;
  }

  const normalized = {
    currency: metaCurrency,
    hotel: {
      base: hotelAmount,
      taxes: null,
      total: hotelAmount,
      currency: hotelCurrency,
    },
    flight: {
      base: flightAmount,
      taxes: null,
      total: flightAmount,
      currency: flightCurrency,
    },
    package: {
      subtotal: null,
      taxes: taxesAmount,
      fees: null,
      total: packageTotalAmount,
      currency: packageTotalCurrency,
    },
    commission: { amount: null, percentage: null },
    display,
    meta: {
      hotelSource,
      packageTotalSource:
        packageTotalAmount != null ? packageTotalSource : "none (not provided)",
    },
  };

  const rawSource = {
    stage,
    dealRootUsed: Boolean(dealRoot),
    dealRootKeys: dealRoot && typeof dealRoot === "object" ? Object.keys(dealRoot) : [],
    dealFareKeys:
      dealRoot?.dealFare && typeof dealRoot.dealFare === "object"
        ? Object.keys(dealRoot.dealFare)
        : [],
    hotelsItemLowest: hotelSearchAmount,
    profilePackageRaw: profileHotelAmount,
    linkedFlightsDealPriceIlsContext:
      linkedFlightsDealPriceIls != null ? linkedFlightsDealPriceIls : null,
  };

  logDynamicPackagePricing("normalized_model", {
    stage,
    normalized,
    rawSource,
    dealComponents,
  });

  if (
    linkedFlightsDealPriceIls != null &&
    Number.isFinite(Number(linkedFlightsDealPriceIls)) &&
    flightAmount == null
  ) {
    logFallbackBlocked(
      "linkedFlightsDealPrice",
      "flight",
      "context flight aggregate exists but is not used — dealFare flight fields missing",
      { linkedFlightsDealPriceIls }
    );
  }

  const searchCardFlightPickFailureReason =
    stage === "search_card" && flightAmount == null
      ? searchCheapestFlight?.unavailableReason ??
        (rowDealRoot ? "row_dealFare_missing_flight_fare" : "no_row_dealFare")
      : null;

  const unavailableReason = resolveFlightPriceUnavailableReason({
    dealRoot,
    dealComponents,
    supplierCurrency: supplierCur,
    linkedFlightsDealPriceIls,
    hasLinkedFlightsStructure,
    normalizedPricingEnabled,
    stage,
    searchCardFlightAmount: stage === "search_card" ? flightAmount : null,
    searchCardFlightCurrency: stage === "search_card" ? flightCurrency : null,
    searchCardFlightPickFailureReason,
  });

  const hotelUnavailableReason = (() => {
    if (hotelAmount != null) return null;
    if (stage === "search_card") {
      if (hotelSearchAmount == null && dealComponents.hotel == null)
        return "missing_lowest_package_and_deal_hotel_fare";
      if (dealComponents.hotel != null && !isIso4217(supplierCur))
        return "supplier_currency_missing_for_deal_hotel";
    }
    if (stage === "profile_summary" || stage === "order_summary") {
      if (profileHotelAmount == null && dealComponents.hotel == null)
        return "missing_package_and_deal_hotel_fare";
      if (profileHotelAmount != null && !isIso4217(profileHotelCur))
        return "package_currency_missing";
      if (dealComponents.hotel != null && !isIso4217(supplierCur))
        return "supplier_currency_missing_for_deal_hotel";
    }
    return "unknown";
  })();

  const taxesUnavailableReason =
    taxesAmount == null ? "missing_dealFare_tax_or_fee_fields" : null;

  const totalUnavailableReason =
    packageTotalAmount == null ? "missing_dealFare_totalFare_totalPrice" : null;

  const pricingTrace = {
    unavailableReason,
    flightUnavailableReason: unavailableReason,
    hotelUnavailableReason,
    taxesUnavailableReason,
    totalUnavailableReason,
    renderedFlightPrice: display.flightText,
    renderedHotelPrice: display.hotelText,
    renderedTaxesPrice: display.taxesText,
    renderedTotalPrice: display.packageTotalText,
    flightAmount,
    hotelAmount,
    packageTotalAmount,
    supplierCurrency: supplierCur,
    hotelSource: normalized.meta?.hotelSource ?? null,
    packageTotalSource: normalized.meta?.packageTotalSource ?? null,
    searchCardFlightSource,
    searchRawFlightSources: searchCheapestFlight?.rawFlightSources ?? null,
  };

  return { normalized, rawSource, pricingTrace };
}

/**
 * Rows for order summary / profile (Hebrew labels).
 */
export function dynamicPackagePricingRowsHe(normalized) {
  const d = normalized?.display;
  if (!d) return [];
  return [
    { key: "flight", label: "מחיר טיסה", value: d.flightText },
    { key: "hotel", label: "מחיר מלון", value: d.hotelText },
    { key: "taxes", label: "מיסים / היטלים / עמלות", value: d.taxesText },
    { key: "total", label: "סה\"כ חבילה", value: d.packageTotalText },
  ];
}

/**
 * פרופיל חבילה דינמית בלבד — כשאין SSOT, ממיר שדות מספריים ב-normalized ל־ILS לתצוגה (לא משנה את המודל).
 */
export function buildDynamicPackageProfileDpNormalizedBreakdownRowsIls(
  normalized,
  profilePriceContext
) {
  if (!normalized || typeof normalized !== "object") return [];
  const u = DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE;
  const supCur = normalized.currency;

  const fmt = (amt, cur) => {
    if (amt == null || !Number.isFinite(Number(amt)) || Number(amt) <= 0) return u;
    const c =
      cur != null && String(cur).trim() !== ""
        ? String(cur).trim().toUpperCase()
        : supCur != null && String(supCur).trim() !== ""
          ? String(supCur).trim().toUpperCase()
          : null;
    if (!c) return u;
    const fx = applyFXSettlement({
      amount: amt,
      fromCurrency: c,
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    if (fx.convertedAmount == null) return u;
    return formatIlsDisplay(fx.convertedAmount);
  };

  return [
    {
      key: "flight",
      label: "מחיר טיסה",
      value: fmt(normalized.flight?.total, normalized.flight?.currency),
    },
    {
      key: "hotel",
      label: "מחיר מלון",
      value: fmt(normalized.hotel?.total, normalized.hotel?.currency),
    },
    {
      key: "taxes",
      label: "מיסים / היטלים / עמלות",
      value: fmt(normalized.package?.taxes, supCur),
    },
    {
      key: "total",
      label: "סה\"כ חבילה",
      value: fmt(normalized.package?.total, normalized.package?.currency),
    },
  ];
}

/**
 * תוצאות חיפוש — לא להציג 3× "לא זמין" + סה"כ; רק שורות עם ערך + סה"כ, או סה"כ + הערה.
 */
export function buildDynamicPackageSearchResultBreakdownRows(normalized) {
  const d = normalized?.display;
  const u = DYNAMIC_PACKAGE_PRICE_UNAVAILABLE_HE;
  if (!d) return [];

  const totalOk = Boolean(d.packageTotalText && d.packageTotalText !== u);
  if (!totalOk) {
    return dynamicPackagePricingRowsHe(normalized);
  }

  const detailRows = [
    { key: "flight", label: "מחיר טיסה", value: d.flightText },
    { key: "hotel", label: "מחיר מלון", value: d.hotelText },
    { key: "taxes", label: "מיסים / היטלים / עמלות", value: d.taxesText },
  ];
  const goodDetails = detailRows.filter((r) => r.value && r.value !== u);
  const badCount = detailRows.length - goodDetails.length;

  if (badCount >= 2) {
    const rows = goodDetails.map((r) => ({ ...r }));
    rows.push({ key: "total", label: "סה\"כ חבילה", value: d.packageTotalText });
    if (goodDetails.length === 0) {
      rows.push({
        key: "hint",
        label: "פירוט",
        value: "פירוט מלא לא זמין כרגע.",
      });
    }
    return rows;
  }

  return dynamicPackagePricingRowsHe(normalized);
}
